/**
 * Pure helpers for building shape paths. No React, no DOM.
 *
 * Every preset works in pixel space over a box that has already been inset by
 * half the stroke width, so a stroked path never escapes the shape's bounds.
 */

/** Trim float noise so path strings are stable and comparable in tests. */
export const fmt = (n) => Math.round(n * 1000) / 1000;

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/** Build a closed straight-edged path from [[x, y], ...]. */
export const polygon = (points) =>
  points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${fmt(x)} ${fmt(y)}`)
    .join(' ') + ' Z';

/**
 * Build a projector mapping unit-space points into the box so that the point
 * set's own bounds fill it exactly.
 *
 * PowerPoint normalises preset geometry to its bounding box. Most regular
 * figures do not naturally fill a box — a pentagon's base sits at sin(54 deg)
 * and a star's points at cos(18 deg) — so without this they would render inside
 * a permanent dead margin and the resize handles would not hug them.
 */
export const boxProjector = (points, b) => {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  return ([x, y]) => [
    b.x + ((x - minX) / spanX) * b.width,
    b.y + ((y - minY) / spanY) * b.height,
  ];
};

/** Vertices of a regular n-gon on the unit circle, first vertex at the top. */
export const regularPolygonPoints = (n) =>
  Array.from({ length: n }, (_, i) => {
    const angle = ((-90 + (i * 360) / n) * Math.PI) / 180;
    return [Math.cos(angle), Math.sin(angle)];
  });

/** A point on an ellipse centred in the box, at `turns` of a full revolution. */
export const ellipsePoint = (b, turns) => {
  const angle = turns * 2 * Math.PI;
  return [
    b.x + b.width / 2 + (b.width / 2) * Math.cos(angle),
    b.y + b.height / 2 + (b.height / 2) * Math.sin(angle),
  ];
};

/**
 * Axis-aligned bounds of a path's anchor points. Test-only helper: it reads the
 * M/L/A/C endpoint coordinates, which is enough to assert a shape fills its box
 * for presets whose extremes land on anchors. Shapes that reach their extremes
 * mid-arc (ellipse, cylinder) are asserted on their arc geometry instead.
 */
export const pathBounds = (d) => {
  const nums = d.match(/-?\d+(\.\d+)?/g)?.map(Number) ?? [];
  const commands = d.match(/[MLACZ]/g) ?? [];
  const xs = [];
  const ys = [];
  let i = 0;

  commands.forEach((cmd) => {
    if (cmd === 'Z') return;
    if (cmd === 'M' || cmd === 'L') {
      xs.push(nums[i]);
      ys.push(nums[i + 1]);
      i += 2;
    } else if (cmd === 'A') {
      // rx ry rotation large-arc sweep x y — only the final pair is an anchor.
      xs.push(nums[i + 5]);
      ys.push(nums[i + 6]);
      i += 7;
    } else if (cmd === 'C') {
      // Two control points then the anchor; controls bound the curve too.
      xs.push(nums[i], nums[i + 2], nums[i + 4]);
      ys.push(nums[i + 1], nums[i + 3], nums[i + 5]);
      i += 6;
    }
  });

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
};
